import { QuestionPaperTemplateEnum } from "@/enums/activity";
import { AppNamesEnum } from "@/enums/appSettings";
import { handleApiError, handleApiResponse } from "@/methods/api";
import ApiVerified from "@/services/apiVerified";
import {
  ActivityAssetWithConfig,
  ActivityManualMarksData,
  ActivityOmrSummaryDisplay,
  ActivityProcessedOmr,
  ActivitySeriesProps,
  ActivityStudentWiseMarks,
  OmrTemplate,
} from "@/types/activity";
import {
  ApiResponse,
  AzureUploadUrl,
  AzureUploadUrlWithUUID,
  PdfAssetResponse,
} from "@/types/global";

export default class ActivityOfflineApi extends ApiVerified {
  // ──────────────────────────────────────────────
  // 1. PDF Generation
  // ──────────────────────────────────────────────

  async getActivityQuestionPdf(
    activityId: number,
    affiliationId: number,
    templateName: QuestionPaperTemplateEnum,
    columnCount: number,
    skipAccessCheck: boolean = false,
    appName: AppNamesEnum,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PdfAssetResponse>(
        `activity-offline/get-activity-question-pdf`,
        {
          params: {
            activityId: activityId,
            affiliationId: affiliationId,
            templateName: templateName,
            columnCount: columnCount,
            skipAccessCheck: skipAccessCheck,
            appName: appName,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getActivityAnswerKeyPdf(
    activityId: number,
    affiliationId: number,
    skipAccessCheck: boolean = false,
    appName: AppNamesEnum,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PdfAssetResponse>(
        `activity-offline/get-activity-answer-key-pdf`,
        {
          params: {
            activityId: activityId,
            affiliationId: affiliationId,
            skipAccessCheck: skipAccessCheck,
            appName: appName,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getActivitySolutionsPdf(
    activityId: number,
    affiliationId: number,
    skipAccessCheck: boolean = false,
    appName: AppNamesEnum,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PdfAssetResponse>(
        `activity-offline/get-activity-solutions-pdf`,
        {
          params: {
            activityId: activityId,
            affiliationId: affiliationId,
            skipAccessCheck: skipAccessCheck,
            appName: appName,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getActivityPdfAsset(mongoId: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PdfAssetResponse>(
        `activity-offline/get-activity-pdf-asset`,
        { params: { mongoId: mongoId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ──────────────────────────────────────────────
  // 2. OMR Processing
  // ──────────────────────────────────────────────

  async getActivityOmrImage(
    activityId: number,
    affiliationId: number,
    numericalAsInteger: boolean,
    isThreeDigitRollNumber: boolean,
    appName: AppNamesEnum,
  ): Promise<ApiResponse> {
    try {
      // activity-offline.json `ActivityOmrImageInputs` — body field is PascalCase `AppName`,
      // unlike the sibling PDF GETs which take a camelCase `appName` query param.
      const response = await this.axiosInstance.post<string>(
        `activity-offline/get-activity-omr-image`,
        {
          ActivityId: activityId,
          AffiliationId: affiliationId,
          NumericalAsInteger: numericalAsInteger,
          IsThreeDigitRollNumber: isThreeDigitRollNumber,
          AppName: appName,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getActivityAssetForBatch(
    activityId: number,
    batchId: number,
    pdfAssetType: string,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<ActivityAssetWithConfig>(
        `activity-offline/get-activity-asset-for-batch`,
        { params: { activityId: activityId, batchId: batchId, pdfAssetType: pdfAssetType } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getActivitySeriesProps(activityId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<ActivitySeriesProps>(
        `activity-offline/get-activity-series-props`,
        { params: { activityId: activityId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getOmrTemplatesForActivity(
    activityId: number,
    numericalAsInteger: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<OmrTemplate>>(
        `activity-offline/get-activity-omr-templates`,
        { params: { activityId: activityId, numericalAsInteger: numericalAsInteger } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getBulkOmrUploadUrl(
    activityId: number,
    batchId: number,
    contentType: string,
    fileMd5Checksum: string,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<AzureUploadUrlWithUUID>(
        `activity-offline/get-bulk-omr-upload-url`,
        {
          params: {
            activityId: activityId,
            batchId: batchId,
            contentType: contentType,
            fileMd5Checksum: fileMd5Checksum,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async submitBulkOmrsForProcessing(
    activityId: number,
    batchId: number,
    uploadUUID: string,
    uploadedFileUrl: string,
    contentType: string,
    fileMd5Checksum: string,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `activity-offline/submit-bulk-omrs-for-processing`,
        {
          ActivityId: activityId,
          BatchId: batchId,
          UploadUUID: uploadUUID,
          UploadedFileUrl: uploadedFileUrl,
          ContentType: contentType,
          FileMd5Checksum: fileMd5Checksum,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getStudentOmrUploadUrl(
    activityId: number,
    batchId: number,
    userId: number,
    contentType: string,
    fileMd5Checksum: string,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<AzureUploadUrl>(
        `activity-offline/get-student-omr-upload-url`,
        {
          params: {
            activityId: activityId,
            batchId: batchId,
            userId: userId,
            contentType: contentType,
            fileMd5Checksum: fileMd5Checksum,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async submitStudentOmrForProcessing(
    activityId: number,
    batchId: number,
    userId: number,
    uploadedFileUrl: string,
    contentType: string,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<ActivityProcessedOmr>(
        `activity-offline/submit-student-omr-for-processing`,
        {
          ActivityId: activityId,
          BatchId: batchId,
          UserId: userId,
          UploadedFileUrl: uploadedFileUrl,
          ContentType: contentType,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async checkOfflineActivityReviewAccess(
    activityId: number,
    affiliationId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<boolean>(
        `activity-offline/check-offline-activity-review-access`,
        {
          params: { activityId: activityId, affiliationId: affiliationId },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getActivityOmrSummary(activityId: number, batchId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<ActivityOmrSummaryDisplay>(
        `activity-offline/get-activity-omr-summary`,
        { params: { activityId: activityId, batchId: batchId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getActivityOmrSummariesByStatus(
    affiliationId: number,
    offlineActivityStatusId: number,
    minDate: string,
    maxDate: string,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<ActivityOmrSummaryDisplay>>(
        `activity-offline/get-activity-omr-summaries-by-status`,
        {
          params: {
            affiliationId: affiliationId,
            offlineActivityStatusId: offlineActivityStatusId,
            minDate: minDate,
            maxDate: maxDate,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getActivityProcessedOmrs(activityId: number, batchId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<ActivityProcessedOmr>>(
        `activity-offline/get-activity-processed-omrs`,
        { params: { activityId: activityId, batchId: batchId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveEditedProcessedOmrs(
    activityId: number,
    batchId: number,
    omrSummary: ActivityOmrSummaryDisplay,
    processedOmrs: Array<ActivityProcessedOmr>,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `activity-offline/save-edited-processed-omrs`,
        {
          ActivityId: activityId,
          BatchId: batchId,
          OmrSummary: omrSummary,
          ProcessedOmrs: processedOmrs,
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async finalizeOmrResults(activityId: number, batchId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `activity-offline/finalize-omr-results`,
        { ActivityId: activityId, BatchId: batchId },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async deleteBulkOmrUpload(
    activityId: number,
    batchId: number,
    uploadUUID: string,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.delete<boolean>(
        `activity-offline/delete-bulk-omr-upload`,
        { params: { activityId: activityId, batchId: batchId, uploadUUID: uploadUUID } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async reprocessOmrs(
    activityId: number,
    affiliationId: number,
    batchId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(`activity-offline/reprocess-omrs`, {
        ActivityId: activityId,
        AffiliationId: affiliationId,
        BatchId: batchId,
      });
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async unfinalizeOfflineActivity(
    activityId: number,
    batchId: number,
    affiliationId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `activity-offline/unfinalize-offline-activity`,
        { ActivityId: activityId, BatchId: batchId, AffiliationId: affiliationId },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // ──────────────────────────────────────────────
  // 3. Manual Marks
  // ──────────────────────────────────────────────

  async getActivityManualMarksData(activityId: number, batchId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<ActivityManualMarksData>(
        `activity-offline/get-activity-manual-marks-data`,
        { params: { activityId: activityId, batchId: batchId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getActivityManualMarksMarksheet(activityId: number, batchId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<ActivityStudentWiseMarks>>(
        `activity-offline/get-activity-manual-marks-marksheet`,
        { params: { activityId: activityId, batchId: batchId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async saveActivityManualMarksData(
    manualMarksData: ActivityManualMarksData,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `activity-offline/save-activity-manual-marks-data`,
        manualMarksData,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async createActivityManualMarksPdfReport(
    activityId: number,
    batchId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<string>(
        `activity-offline/create-activity-manual-marks-pdf-report`,
        { ActivityId: activityId, BatchId: batchId },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
}
